import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useKeyboardLift } from '../../hooks/useKeyboardLift';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Jitsi } from 'capacitor-jitsi-meet';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { auth, db } from '../../auth/firebase';
import {
  IonPage,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonButton,
  IonIcon,
  IonInput,
  IonAvatar,
  IonGrid,
  IonRow,
  IonCol,
  IonFooter,
  IonText,
  IonFab,
} from '@ionic/react';
import { ellipsisVertical, send, chevronBack, homeOutline, search as searchIcon, closeOutline, happyOutline, attachOutline, cameraOutline, micOutline, callOutline, videocamOutline } from 'ionicons/icons';
import BasePageWithHeader from '../../components/BasePageWithHeader';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import styles from '../TeamMessagingPage.module.scss';
import inboxStyles from '../Inbox.module.scss';
import contactsStyles from '../bookingSystem/ContactsList.module.scss';
import bookingsListStyles from '../../components/BookingsListModal.module.scss';
import { useTranslation } from 'react-i18next';

interface MessageStub { id: string | number; sender: string; text: string; timestamp: Date }
interface ConvStub { id: string | number; name: string; members: string[]; unread: number; lastMessage?: string; messages: MessageStub[]; avatarUrl?: string }
interface NewChatState {
  newChat?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    avatarUrl?: string;
  };
  convId?: string | number;
}

const sampleConvs = (): ConvStub[] => [
  {
    id: 'team-general',
    name: 'Team - General',
    members: ['Alice', 'Ben', 'Clara', 'Dan'],
    unread: 2,
    lastMessage: 'Welcome everyone, standup at 10:00.',
    messages: [
      { id: 1, sender: 'Alice', text: 'Welcome everyone, standup at 10:00.', timestamp: new Date(Date.now() - 1000 * 60 * 30) },
      { id: 2, sender: 'You', text: 'Got it, I will be there.', timestamp: new Date(Date.now() - 1000 * 60 * 22) },
      { id: 3, sender: 'Ben', text: 'I posted the agenda in files.', timestamp: new Date(Date.now() - 1000 * 60 * 14) },
    ],
  },
  {
    id: 'team-schedule',
    name: 'Team - Schedule',
    members: ['Clara', 'Eli'],
    unread: 0,
    lastMessage: 'Shift posted for Sunday cleaning.',
    messages: [
      { id: 4, sender: 'Clara', text: 'Shift posted for Sunday cleaning.', timestamp: new Date(Date.now() - 1000 * 60 * 50) },
      { id: 5, sender: 'You', text: 'Confirmed. I can take second slot.', timestamp: new Date(Date.now() - 1000 * 60 * 36) },
    ],
  },
];

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const getJitsiRoomName = (conversation: ConvStub): string => {
  const normalizedId = String(conversation.id)
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const fallback = conversation.name
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return `rsmaids-mobile-${normalizedId || fallback || 'team-room'}`;
};

const createFreshJitsiRoomName = (conversation: ConvStub, audioOnly: boolean): string => {
  const base = getJitsiRoomName(conversation);
  const callKind = audioOnly ? 'voice' : 'video';
  const uniqueSuffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  return `${base}-${callKind}-${uniqueSuffix}`;
};

const logJitsiDebug = (...args: unknown[]) => {
  console.info('[TeamMessagingMobile][Jitsi]', ...args);
};

const TeamMessagingMobile: React.FC = () => {
  const { t } = useTranslation();
  const FUNCTIONS_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net';
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState<ConvStub[]>(() => sampleConvs());
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState<string | number | null>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [incomingEmployeeCall, setIncomingEmployeeCall] = useState<null | {
    sessionId: string;
    roomName: string;
    callerName: string;
    calleeId: string;
  }>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);
  const jitsiLaunchInFlightRef = useRef(false);
  // Lift the floating search bar above the native keyboard (same as the other search bars)
  useKeyboardLift({ bars: () => [overlayRef.current], scroll: () => gridRef.current });

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', selected ? '1' : String(progress));
  }, [selected]);

  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  const blurAndNavigate = useCallback((to: string) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
    navigate(to, { replace: true });
  }, [navigate]);

  const updateOverlayPosition = useCallback(() => {
    if (!overlayRef.current) return;
    const viewportWidth = window.innerWidth;
    if (isSearchVisible) {
      const desiredWidth = viewportWidth - 32;
      overlayRef.current.style.setProperty('--overlay-w', `${desiredWidth}px`);
      overlayRef.current.style.setProperty('--overlay-left', '16px');
    } else {
      overlayRef.current.style.setProperty('--overlay-w', '56px');
      const centerLeft = (viewportWidth / 2) - 28;
      overlayRef.current.style.setProperty('--overlay-left', `${centerLeft}px`);
    }
  }, [isSearchVisible]);

  const handleToggleSearch = useCallback(() => {
    if (isSearchVisible) {
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearch('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      window.setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220);
      return;
    }

    setIsSearchHidden(false);
    setIsSearchVisible(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateOverlayPosition();
        try {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (getter) {
            getter().then((inputEl: HTMLInputElement) => {
              try { inputEl.focus(); } catch { }
            }).catch(() => { });
          }
        } catch { }
      });
    });
  }, [isSearchVisible, updateOverlayPosition]);

  // Selecting the conversation passed via router state is computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as a setState call inside an effect.
  const [prevLocationState, setPrevLocationState] = useState(location.state);
  if (prevLocationState !== location.state) {
    setPrevLocationState(location.state);
    const state = location.state as { convId?: string | number } | undefined;
    if (state?.convId) setSelected(state.convId);
  }

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    if (selected) {
      baseHeaderRef.current?.style.setProperty('--title-opacity', '1');
    }
  }, [selected]);

  useEffect(() => {
    document.body.classList.toggle('team-message-open', Boolean(selected));
    return () => {
      document.body.classList.remove('team-message-open');
    };
  }, [selected]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  const locationState = location.state as NewChatState | undefined;
  const newChatConversation = locationState?.newChat
    ? {
        id: `new:${locationState.newChat.id}`,
        name: locationState.newChat.name,
        members: [locationState.newChat.name],
        unread: 0,
        lastMessage: locationState.newChat.email || locationState.newChat.phone || 'New chat',
        messages: [],
        avatarUrl: locationState.newChat.avatarUrl,
      }
    : null;

  const visibleConversations = newChatConversation
    ? [newChatConversation, ...conversations.filter(conv => conv.id !== newChatConversation.id)]
    : conversations;

  const selectedConversation = selected
    ? visibleConversations.find(c => c.id === selected)
    : null;

  const openConv = (id: string | number) => {
    setSelected(id);
    setConversations((prev) => prev.map((conv) => (
      conv.id === id ? { ...conv, unread: 0 } : conv
    )));
  };

  const sendMessage = () => {
    if (!selected || !message.trim()) return;
    setConversations(prev => prev.map(c => c.id === selected ? ({ ...c, messages: [...c.messages, { id: Date.now(), sender: 'You', text: message.trim(), timestamp: new Date() }], lastMessage: message.trim() }) : c));
    setMessage('');
  };

  const updateEmployeeCallSessionStatus = async (sessionId: string, status: 'accepted' | 'declined' | 'completed') => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const idToken = await currentUser.getIdToken();
    const response = await fetch(`${FUNCTIONS_URL}/updateEmployeeCallSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ sessionId, status }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Employee call status update failed (${response.status}): ${errText}`);
    }
  };

  const createEmployeeCallSession = async (conversation: ConvStub, audioOnly: boolean, overrideRoomName?: string): Promise<{ sessionId: string; roomName: string } | null> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert(t('pages.teamMessaging.TeamMessagingMobile.youMustBeLoggedInTo'));
      return null;
    }

    const idToken = await currentUser.getIdToken();
    const calleeId = String(currentUser.uid || (conversation.id ?? '')).trim();
    const roomName = overrideRoomName || createFreshJitsiRoomName(conversation, audioOnly);

    const response = await fetch(`${FUNCTIONS_URL}/createEmployeeCallSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        calleeId,
        roomName,
        callType: audioOnly ? 'voice' : 'video',
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Employee call session creation failed (${response.status}): ${errText}`);
    }

    const payload = await response.json() as { sessionId?: string; roomName?: string };
    if (!payload.sessionId || !payload.roomName) {
      throw new Error('Employee call session was created without a room or session ID.');
    }

    return { sessionId: payload.sessionId, roomName: payload.roomName };
  };

  const openJitsiCall = async (conversation: ConvStub, audioOnly: boolean, overrideRoomName?: string) => {
    if (jitsiLaunchInFlightRef.current) {
      logJitsiDebug('launch already in flight; ignoring duplicate open request');
      return;
    }

    const rawDomain = (import.meta.env.VITE_JITSI_DOMAIN as string | undefined)?.trim();
    const jaasAppId = (import.meta.env.VITE_JITSI_JAAS_APP_ID as string | undefined)?.trim();

    const domain = (jaasAppId ? '8x8.vc' : (rawDomain || 'meet.jit.si'))
      .replace(/^https?:\/\//i, '')
      .replace(/\/+$/, '');

    const roomName = overrideRoomName || createFreshJitsiRoomName(conversation, audioOnly);
    const roomPath = jaasAppId ? `${jaasAppId}/${roomName}` : roomName;
    // i18n-ignore: sent to the Jitsi server as the participant name other callers see — stays English
    const displayName = 'RSMaids Team';
    const webUrl = `https://${domain}/${roomPath}#config.prejoinPageEnabled=false&config.startWithVideoMuted=${audioOnly ? 'true' : 'false'}&config.startAudioOnly=${audioOnly ? 'true' : 'false'}&config.disableDeepLinking=true&config.enableWelcomePage=false&interfaceConfig.MOBILE_APP_PROMO=false`;

    logJitsiDebug('launch request', {
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      jaasAppId,
      rawDomain,
      domain,
      roomName,
      roomPath,
      audioOnly,
      conversationId: conversation.id,
      webUrl,
    });

    if (Capacitor.isNativePlatform()) {
      jitsiLaunchInFlightRef.current = true;
      let joined = false;
      let ended = false;
      let noJoinTimer: number | null = null;
      const clearJoinWarning = () => {
        if (noJoinTimer !== null) {
          window.clearTimeout(noJoinTimer);
          noJoinTimer = null;
        }
      };
      const releaseLaunchGate = () => {
        jitsiLaunchInFlightRef.current = false;
      };
      const cleanupListeners = () => {
        window.removeEventListener('onConferenceJoined', onConferenceJoined);
        window.removeEventListener('onConferenceTerminated', onConferenceEnded);
        window.removeEventListener('onConferenceLeft', onConferenceEnded);
        window.removeEventListener('onConferenceWillLeave', onConferenceEnded);
        clearJoinWarning();
      };
      const onConferenceJoined = () => {
        joined = true;
        logJitsiDebug('native conference joined event received', { roomPath, jaasAppId, domain });
        cleanupListeners();
      };
      const onConferenceEnded = () => {
        ended = true;
        logJitsiDebug('native conference ended/left event received', { roomPath, jaasAppId, domain, joined });
        cleanupListeners();
        releaseLaunchGate();
      };

      window.addEventListener('onConferenceJoined', onConferenceJoined, { once: true });
      window.addEventListener('onConferenceTerminated', onConferenceEnded, { once: true });
      window.addEventListener('onConferenceLeft', onConferenceEnded, { once: true });
      window.addEventListener('onConferenceWillLeave', onConferenceEnded, { once: true });

      try {
        let joinToken: string | undefined;

        if (jaasAppId) {
          const currentUser = auth.currentUser;
          const idToken = await currentUser?.getIdToken();
          logJitsiDebug('requesting JaaS token', { roomName, hasUser: Boolean(currentUser), userUid: currentUser?.uid, functionsUrl: `${FUNCTIONS_URL}/getJitsiJoinToken` });
          if (!idToken) {
            window.alert(t('pages.teamMessaging.TeamMessagingMobile.youMustBeLoggedInTo2'));
            return;
          }

          const tokenResp = await fetch(
            `${FUNCTIONS_URL}/getJitsiJoinToken?room=${encodeURIComponent(roomName)}`,
            { headers: { Authorization: `Bearer ${idToken}` } }
          );

          logJitsiDebug('JaaS token HTTP response', { roomName, status: tokenResp.status, ok: tokenResp.ok, headers: Object.fromEntries(tokenResp.headers.entries()) });

          if (!tokenResp.ok) {
            const errText = await tokenResp.text();
            logJitsiDebug('JaaS token fetch failed', { roomName, status: tokenResp.status, errorText: errText });
            throw new Error(`JaaS token endpoint failed (${tokenResp.status}): ${errText}`);
          }

          const tokenData = await tokenResp.json() as { token?: string; appId?: string; room?: string };
          logJitsiDebug('JaaS token received', { roomName, tokenLength: tokenData.token?.length ?? 0, appId: tokenData.appId, returnedRoom: tokenData.room });
          if (!tokenData.token) {
            throw new Error('JaaS token endpoint returned no token');
          }
          joinToken = tokenData.token;
        }

        const visibilityBeforeLaunch = document.visibilityState;
        logJitsiDebug('invoking native Jitsi join', {
          domain,
          roomPath,
          tokenPresent: Boolean(joinToken),
          audioOnly,
          displayName,
        });

        const result = await Jitsi.joinConference({
          roomName: roomPath,
          url: `https://${domain}`,
          token: joinToken,
          displayName,
          startWithAudioMuted: audioOnly,
          startWithVideoMuted: audioOnly,
          featureFlags: {
            'prejoinpage.enabled': false,
            'recording.enabled': false,
            'live-streaming.enabled': false,
            'android.screensharing.enabled': false,
            'welcomepage.enabled': false,
            'invite.enabled': false,
          },
          configOverrides: {
            startWithAudioMuted: audioOnly,
            startWithVideoMuted: audioOnly,
            prejoinConfig: { enabled: false },
            disableDeepLinking: true,
            enableWelcomePage: false,
            lobby: { enable: false },
          },
        });

        logJitsiDebug('native Jitsi join returned', { result, success: result?.success, roomPath, domain, tokenPresent: Boolean(joinToken) });

        if (result?.success === false) {
          throw new Error('Native Jitsi plugin returned success=false');
        }

        // If native UI does not take focus shortly after the plugin call,
        // surface it immediately so we can collect logs from a known failure.
        window.setTimeout(() => {
          if (document.visibilityState === visibilityBeforeLaunch) {
            logJitsiDebug('native Jitsi did not regain focus after launch; likely stuck in auth or lobby state', { visibilityBeforeLaunch, currentVisibility: document.visibilityState, roomPath, domain });
            window.alert(t('pages.teamMessaging.TeamMessagingMobile.jitsiCallDidNotOpenPlease'));
          }
        }, 1500);

        // Jitsi can open successfully but remain in guest lobby/auth wait state.
        // If conference does not emit joined soon, surface probable server-side cause.
        noJoinTimer = window.setTimeout(() => {
          if (!joined && !ended) {
            cleanupListeners();
            releaseLaunchGate();

            const tenantHint = jaasAppId
              ? t('pages.teamMessaging.TeamMessagingMobile.jaasAppid', { jaasAppId })
              : `domain ${domain}`;
            logJitsiDebug('Jitsi join timeout reached without native joined event', { roomPath, domain, jaasAppId, ended, joined });
            window.alert(t('pages.teamMessaging.TeamMessagingMobile.meetingDidNotFullyJoinIs', { tenantHint, roomPath }));
          }
        }, 10000);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[TeamMessagingMobile] Jitsi launch failed:', error);
        cleanupListeners();
        releaseLaunchGate();
        logJitsiDebug('Jitsi launch failed with exception', { message, roomPath, domain, jaasAppId });
        window.alert(t('pages.teamMessaging.TeamMessagingMobile.jitsiLaunchFailed', { message }));
      }
      return;
    }

    logJitsiDebug('falling back to browser web open', { roomPath, domain, webUrl });
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    let unsubscribeSessionListener: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeSessionListener) {
        unsubscribeSessionListener();
        unsubscribeSessionListener = undefined;
      }

      if (!user) {
        setIncomingEmployeeCall(null);
        return;
      }

      const sessionsRef = collection(db, 'employeeCallSessions');
      const q = query(
        sessionsRef,
        where('calleeId', '==', user.uid),
        where('status', '==', 'ringing'),
        orderBy('createdAt', 'desc')
      );

      unsubscribeSessionListener = onSnapshot(q, (snapshot) => {
        const active = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Record<string, unknown>) }))
          .filter((session) => String((session as { callerId?: string }).callerId || '') !== user.uid)
          .sort((a, b) => {
            const aTime = Number((a as { createdAt?: { seconds?: number } }).createdAt?.seconds ?? 0);
            const bTime = Number((b as { createdAt?: { seconds?: number } }).createdAt?.seconds ?? 0);
            return bTime - aTime;
          })[0];

        if (!active) {
          setIncomingEmployeeCall(null);
          return;
        }

        setIncomingEmployeeCall({
          sessionId: String((active as { sessionId?: string }).sessionId || active.id),
          roomName: String((active as { roomName?: string }).roomName || ''),
          callerName: String((active as { callerName?: string }).callerName || 'Employee'),
          calleeId: String((active as { calleeId?: string }).calleeId || user.uid),
        });
      }, (error) => {
        console.error('[TeamMessagingMobile] employee-call listener failed:', error);
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSessionListener) {
        unsubscribeSessionListener();
      }
    };
  }, []);

  const acceptIncomingEmployeeCall = async () => {
    if (!incomingEmployeeCall) return;

    try {
      await updateEmployeeCallSessionStatus(incomingEmployeeCall.sessionId, 'accepted');
      await openJitsiCall({
        id: incomingEmployeeCall.calleeId,
        name: incomingEmployeeCall.callerName,
        members: [incomingEmployeeCall.callerName],
        unread: 0,
        messages: [],
      }, false, incomingEmployeeCall.roomName);
      setIncomingEmployeeCall(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TeamMessagingMobile] acceptIncomingEmployeeCall failed:', error);
      window.alert(t('pages.teamMessaging.TeamMessagingMobile.couldNotAcceptTheCall', { message }));
    }
  };

  const declineIncomingEmployeeCall = async () => {
    if (!incomingEmployeeCall) return;

    try {
      await updateEmployeeCallSessionStatus(incomingEmployeeCall.sessionId, 'declined');
      setIncomingEmployeeCall(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TeamMessagingMobile] declineIncomingEmployeeCall failed:', error);
      window.alert(t('pages.teamMessaging.TeamMessagingMobile.couldNotDeclineTheCall', { message }));
    }
  };

  if (selected) {
    const conv = selectedConversation ?? visibleConversations.find(c => c.id === selected)!;
    const memberSummary = conv.members.length > 0 ? conv.members.join(', ') : 'Members';
    return (
      <IonPage className={inboxStyles.page}>
        <BasePageWithHeader
          ref={baseHeaderRef}
          title={conv.name}
          avatarUrl={conv.avatarUrl}
          avatarInitials={getInitials(conv.name)}
          headerAvatarClassName={styles.mobileChatHeaderAvatar}
          titleNode={
            <IonCol size="auto" className={styles.mobileChatHeaderMeta}>
              <IonText className={styles.mobileChatHeaderTitleText}>{conv.name}</IonText>
              <IonText className={styles.mobileChatHeaderMembersText}>{memberSummary}</IonText>
            </IonCol>
          }
          toolbarClassName={contactsStyles.toolbar}
          smallTitleClassName={`${contactsStyles.smallTitle} ${styles.mobileChatSmallTitle}`}
          titleHeaderClassName={styles.mobileChatTitleHeader}
          startButtons={
            <>
              <IonButton
                shape="round"
                className={contactsStyles.backButton}
                onClick={() => setSelected(null)}
                aria-label={t('pages.teamMessaging.TeamMessagingMobile.back')}
              >
                <IonIcon icon={chevronBack} />
              </IonButton>
            </>
          }
          endButtons={
            <>
              <IonButton
                shape="round"
                fill="clear"
                className={`${contactsStyles.searchButton} ${styles.headerActionButton}`}
                onClick={async () => {
                  try {
                    const roomName = createFreshJitsiRoomName(conv, true);
                    await createEmployeeCallSession(conv, true, roomName);
                    await openJitsiCall(conv, true, roomName);
                  } catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    console.error('[TeamMessagingMobile] createEmployeeCallSession failed:', error);
                    window.alert(t('pages.teamMessaging.TeamMessagingMobile.employeeCallStartingFailed', { message }));
                  }
                }}
                aria-label={t('pages.teamMessaging.TeamMessagingMobile.startVoiceCall')}
              >
                <IonIcon icon={callOutline} />
              </IonButton>
              <IonButton
                shape="round"
                fill="clear"
                className={`${contactsStyles.searchButton} ${styles.headerActionButton}`}
                onClick={async () => {
                  try {
                    const roomName = createFreshJitsiRoomName(conv, false);
                    await createEmployeeCallSession(conv, false, roomName);
                    await openJitsiCall(conv, false, roomName);
                  } catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    console.error('[TeamMessagingMobile] createEmployeeCallSession failed:', error);
                    window.alert(t('pages.teamMessaging.TeamMessagingMobile.employeeCallStartingFailed', { message }));
                  }
                }}
                aria-label={t('pages.teamMessaging.TeamMessagingMobile.startVideoCall')}
              >
                <IonIcon icon={videocamOutline} />
              </IonButton>
              <IonButton
                shape="round"
                className={`${contactsStyles.searchButton} ${styles.headerActionButton}`}
                onClick={() => {}}
              >
                <IonIcon icon={ellipsisVertical} />
              </IonButton>
            </>
          }
        >
          {incomingEmployeeCall && (
            <div style={{ position: 'fixed', left: 16, right: 16, bottom: 128, zIndex: 2000, background: '#262B35', borderRadius: 18, padding: '14px 16px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', color: '#fff' }}>
              <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75, marginBottom: 8 }}>{t('pages.teamMessaging.TeamMessagingMobile.incomingEmployeeCall')}</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{incomingEmployeeCall.callerName}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <IonButton size="small" color="success" onClick={acceptIncomingEmployeeCall}>{t('pages.teamMessaging.TeamMessagingMobile.answer')}</IonButton>
                <IonButton size="small" color="medium" onClick={declineIncomingEmployeeCall}>{t('pages.teamMessaging.TeamMessagingMobile.decline')}</IonButton>
              </div>
            </div>
          )}

          <IonContent scrollY={false} className={`${styles.content} ${styles.mobileChatContent}`}>
            <IonGrid
              ref={gridRef}
              className={`${styles.teamContainer} ${inboxStyles.inboxContainer}`}
              onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
            >
              <IonGrid className={styles.mobileChatPanel}>
                <IonRow className={styles.mobileChatMainCol}>
                  <IonCol className={styles.mobileChatMessagesCard}>
                    <IonList className={styles.mobileChatMessagesContent}>
                      {conv.messages.map((m) => (
                        <IonRow
                          key={String(m.id)}
                          className={`${styles.mobileChatMessageRow} ${m.sender === 'You' ? styles.mobileChatMessageRowSent : styles.mobileChatMessageRowReceived}`}
                        >
                          <IonCol size="12" className={m.sender === 'You' ? inboxStyles.messageColRight : inboxStyles.messageColLeft}>
                            <IonItem
                              lines="none"
                              className={`${inboxStyles.messageItem} ${m.sender === 'You' ? inboxStyles.messageItemMe : inboxStyles.messageItemOther} ${styles.mobileChatMessageItem}`}
                            >
                              {m.sender !== 'You' && (
                                <IonAvatar slot="start" className={`${inboxStyles.messageAvatar} ${styles.mobileChatAvatarAnchor}`} aria-hidden="true">
                                  <IonText className={inboxStyles.avatarText}>{getInitials(m.sender)}</IonText>
                                </IonAvatar>
                              )}
                              <IonLabel className={inboxStyles.messageLabel}>
                                <IonText className={`${styles.mobileChatMessage} ${m.sender === 'You' ? styles.mobileChatSent : styles.mobileChatReceived}`}>
                                  {m.sender !== 'You' && (
                                    <IonText className={styles.mobileChatBubbleSender}>{m.sender}</IonText>
                                  )}
                                  <p className={styles.mobileChatMessageText}>
                                    {m.text}
                                  </p>
                                  <IonText className={styles.mobileChatMessageTime}>{formatTime(m.timestamp)}</IonText>
                                </IonText>
                              </IonLabel>
                            </IonItem>
                          </IonCol>
                        </IonRow>
                      ))}
                    </IonList>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonGrid>
          </IonContent>

          <IonFooter className={styles.composerFooter}>
            <IonGrid className={styles.mobileChatComposerShell}>
              <IonRow className={styles.mobileChatComposer}>
                <IonCol size="auto">
                  <IonButton fill="clear" className={styles.mobileChatComposerIconButton} aria-label={t('pages.teamMessaging.TeamMessagingMobile.emoji')}>
                    <IonIcon icon={happyOutline} />
                  </IonButton>
                </IonCol>
                <IonCol>
                  <IonInput
                    value={message}
                    placeholder={t('pages.teamMessaging.TeamMessagingMobile.message')}
                    onIonChange={e => setMessage(e.detail.value ?? '')}
                    className={styles.mobileChatComposerInput}
                    enterkeyhint="send"
                  />
                </IonCol>
                <IonCol size="auto">
                  <IonButton fill="clear" className={styles.mobileChatComposerIconButton} aria-label={t('pages.teamMessaging.TeamMessagingMobile.attachFile')}>
                    <IonIcon icon={attachOutline} />
                  </IonButton>
                </IonCol>
                <IonCol size="auto">
                  <IonButton fill="clear" className={styles.mobileChatComposerIconButton} aria-label={t('pages.teamMessaging.TeamMessagingMobile.openCamera')}>
                    <IonIcon icon={cameraOutline} />
                  </IonButton>
                </IonCol>
                <IonCol size="auto">
                  <IonButton
                    className={message.trim() ? styles.mobileChatSendButton : styles.mobileChatMicButton}
                    onClick={sendMessage}
                    fill="clear"
                    aria-label={message.trim() ? t('pages.teamMessaging.TeamMessagingMobile.sendMessage') : t('pages.teamMessaging.TeamMessagingMobile.recordVoiceMessage')}
                  >
                    <IonIcon slot="icon-only" icon={message.trim() ? send : micOutline} />
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonFooter>

          <IonFab
            ref={el => { overlayRef.current = el; }}
            className={`${inboxStyles.searchRow} ${isSearchHidden && !isSearchClosing ? bookingsListStyles.hidden : ''} ${bookingsListStyles.searchMorph} ${styles.tabbedSearchMorph} ${isSearchVisible ? bookingsListStyles.searchMorphExpanded : ''} ${isSearchClosing ? bookingsListStyles.searchMorphClosing : ''} ${isSearchHidden ? bookingsListStyles.hidden : ''}`}
            aria-hidden={!isSearchVisible}
          >
            <IonSearchbar
              ref={searchRef}
              value={search}
              debounce={50}
              onIonInput={e => setSearch(e.detail.value ?? '')}
              onIonClear={() => setSearch('')}
              placeholder={t('pages.teamMessaging.TeamMessagingMobile.searchMessages')}
              animated
              showCancelButton="never"
              showClearButton={search ? 'always' : 'never'}
              className={`${bookingsListStyles.searchBar} ${bookingsListStyles.floatingSearchBarInline} ${isSearchVisible ? bookingsListStyles.floatingSearchBarInlineExpanded : ''}`}
            />
          </IonFab>
        </BasePageWithHeader>
      </IonPage>
    );
  }

  return (
      <IonPage className={inboxStyles.page}>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.teamMessaging.TeamMessagingMobile.teamMessaging')}
        toolbarClassName={contactsStyles.toolbar}
        smallTitleClassName={contactsStyles.smallTitle}
        titleHeaderClassName={contactsStyles.titleHeader}
        startButtons={
          <IonRow className={styles.rootStartButtons}>
            <IonButton
              shape="round"
              className={contactsStyles.backButton}
              onClick={() => blurAndNavigate('/dashboard')}
              aria-label={t('pages.teamMessaging.TeamMessagingMobile.appHome')}
            >
              <IonIcon icon={homeOutline} />
            </IonButton>
          </IonRow>
        }
        endButtons={
          <>
            <IonButton
              shape="round"
              className={`${contactsStyles.searchButton} ${styles.headerActionButton}`}
                onClick={handleToggleSearch}
            >
                <IonIcon icon={isSearchVisible ? closeOutline : searchIcon} />
            </IonButton>
            <IonButton
              shape="round"
              className={`${contactsStyles.searchButton} ${styles.headerActionButton}`}
              onClick={() => {}}
            >
                <IonIcon icon={ellipsisVertical} />
            </IonButton>
          </>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <IonGrid
            ref={gridRef}
            className={`${styles.teamContainer} ${inboxStyles.inboxContainer}`}
            onScroll={(e) => {
              const scrollTop = (e.target as HTMLElement).scrollTop || 0;
              handleScroll(scrollTop);
            }}
          >
            <IonGrid className={styles.mobileChatPanel}>
              <IonCol className={styles.mobileChatChannelCol}>
                <IonList className={inboxStyles.conversationList}>
                  {visibleConversations.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.lastMessage || '').toLowerCase().includes(search.toLowerCase())).map(c => (
                    <IonItem key={c.id} button detail={false} onClick={() => openConv(c.id)} className={inboxStyles.conversationItem}>
                      <IonAvatar slot="start" className={inboxStyles.avatar}>
                        {c.avatarUrl ? (
                          <img
                            src={proxyImage(c.avatarUrl) || getDefaultAvatar(c.name)}
                            alt={c.name}
                            onError={(e) => {
                              const imgElement = e.target as HTMLImageElement;
                              imgElement.src = getDefaultAvatar(c.name);
                            }}
                          />
                        ) : (
                          <IonText>{getInitials(c.name)}</IonText>
                        )}
                      </IonAvatar>
                      <IonLabel>
                        <IonText className={inboxStyles.contactName}>
                          <h3>{c.name}</h3>
                        </IonText>
                        <IonText className={inboxStyles.lastMessage}>
                          <p>{c.lastMessage}</p>
                        </IonText>
                      </IonLabel>
                      <IonCol className={inboxStyles.conversationMeta} slot="end">
                        <IonText className={inboxStyles.timestamp}>
                          <p>{formatTime(c.messages[c.messages.length - 1]?.timestamp ?? new Date())}</p>
                        </IonText>
                        <IonRow className={inboxStyles.iconsRow}>
                          {c.unread > 0 && <IonText className={inboxStyles.unreadBadge}>{c.unread}</IonText>}
                        </IonRow>
                      </IonCol>
                    </IonItem>
                  ))}
                </IonList>
              </IonCol>
            </IonGrid>
          </IonGrid>
        </IonContent>

        <IonFab
          ref={el => { overlayRef.current = el; }}
          className={`${inboxStyles.searchRow} ${isSearchHidden && !isSearchClosing ? bookingsListStyles.hidden : ''} ${bookingsListStyles.searchMorph} ${styles.tabbedSearchMorph} ${isSearchVisible ? bookingsListStyles.searchMorphExpanded : ''} ${isSearchClosing ? bookingsListStyles.searchMorphClosing : ''} ${isSearchHidden ? bookingsListStyles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={search}
            debounce={50}
            onIonInput={e => setSearch(e.detail.value ?? '')}
            onIonClear={() => setSearch('')}
            placeholder={t('pages.teamMessaging.TeamMessagingMobile.searchConversations')}
            animated
            showCancelButton="never"
            showClearButton={search ? 'always' : 'never'}
            className={`${bookingsListStyles.searchBar} ${bookingsListStyles.floatingSearchBarInline} ${isSearchVisible ? bookingsListStyles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>
    </IonPage>
  );
};

export default TeamMessagingMobile;

